import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as moment from 'moment-timezone';
import * as utils from '../../utils/utility-functions';
import { LogService } from "../logging.service";
import { RelevantLabService } from "../relevant-lab.service";
import { EmailNotificationService } from "../notification.service";
import { OrgLocation } from "src/app/models/org-location.model";
import { Appointment } from "src/app/models/appointment.model";
import { appointmentlistenum } from "src/app/models/appointment-list-type";
import { LogType } from "src/app/models/log-type";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { UserTypeEnum } from "src/app/models/user-type";
import { NotificationType } from "src/app/models/notification-type.model";
import { ConsultationType } from "src/app/models/consultation-type";

@Injectable({
  providedIn: 'root',
})

export class AppointmentCollectionService {
  private firebaseAppointmentCollection: AngularFirestoreCollection;
  private firebaseAppointmentDoctorMessageCollection: AngularFirestoreCollection;

  _EXPERT = 'Expert';
  _HOSPITAL = 'Hospital';
  _CLOSED = 'closed';
  _REVIEW = 'review';


  private currentUser: any;

  private selectedOrganizationLocation = new OrgLocation;

  private todaysAppointments = new BehaviorSubject([]);
  currentTodaysAppointments = this.todaysAppointments.asObservable();

  private unconfirmedAppointments = new BehaviorSubject([]);
  currentUncomfirmedAppointments = this.unconfirmedAppointments.asObservable();

  private additionalRequestAppointments = new BehaviorSubject([]);
  currentAdditionalRequestAppointments = this.additionalRequestAppointments.asObservable();

  private confirmationRequiredAppointments = new BehaviorSubject([]);
  currentConfirmationRequiredAppointments = this.confirmationRequiredAppointments.asObservable();

  private rescheduleRequestedAppointments = new BehaviorSubject([]);
  currentRescheduleRequestedAppointments = this.rescheduleRequestedAppointments.asObservable();

  private reviewAppointments = new BehaviorSubject([]);
  currentReviewAppointments = this.reviewAppointments.asObservable();

  private confirmedAppointments = new BehaviorSubject([]);
  currentConfirmedAppointments = this.confirmedAppointments.asObservable();

  private closedAppointments = new BehaviorSubject([]);
  currentClosedAppointments = this.closedAppointments.asObservable();

  private needsAttentionAppointments = new BehaviorSubject([]);
  currentNeedsAttentionAppointments = this.needsAttentionAppointments.asObservable();

  private allUpcomingAppointments = new BehaviorSubject([]);
  currentAllUpcomingAppointments = this.allUpcomingAppointments.asObservable();

  private allPastAppointments = new BehaviorSubject([]);
  currentAllPastAppointments = this.allPastAppointments.asObservable();

  private allAppointments = new BehaviorSubject([]);
  currentAllAppointments = this.allAppointments.asObservable();

  private doctorMessages = new BehaviorSubject([]);
  currentDoctorMessages = this.doctorMessages.asObservable();

  private appointment = new BehaviorSubject(new Appointment);
  currentAppointment = this.appointment.asObservable();

  constructor(
    private logService: LogService,
    private notificationService: EmailNotificationService,
    private relevantLabService: RelevantLabService,
    private angularFirestore: AngularFirestore) {
    if (this.currentUser == null) {
      this.currentUser = JSON.parse(localStorage.getItem('doctorUser'));
    }
    if (this.appointment.getValue() == null) {
      this.appointment.next(JSON.parse(localStorage.getItem('currentAppointment')));
    }
  }

  async findAppointmentByID(appointmentID: string) {
    try {
      let appointment = null;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointmentID)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          appointment = docData;
        }).catch(err => console.log("findAppointmentByID query 1 error: " + err));

      this.updateAppointment(appointment);
      return appointment;
    } catch (error) {
      console.log('findAppointmentByID(): ' + JSON.stringify(error));
    }
  }

  async getDoctorMessages(appointmentID: string) {
    try {
      let doctorMessages = [];

      if (this.firebaseAppointmentDoctorMessageCollection == null) {
        this.firebaseAppointmentDoctorMessageCollection = this.angularFirestore.collection('Appointments').doc(appointmentID).collection("DoctorMessages");
      }


      await this.firebaseAppointmentDoctorMessageCollection.ref
        .orderBy('created_at', 'desc')
        .get()
        .then(
          querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData = {
                id: doc.id,
                ...doc.data()
              }
              doctorMessages.push(docData)
            });
          }).catch(err => console.log("getDoctorMessages query 1 error: " + err));

      this.doctorMessages.next(doctorMessages);
      return doctorMessages;
    } catch (error) {
      console.log('getDoctorMessages(): ' + JSON.stringify(error));
    }
  }

  async getAllAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      if (this.selectedOrganizationLocation.id != null) {
        await this.firebaseAppointmentCollection.ref
          .where('doctor_id', '==', this.currentUser.id)
          .where('org_location_id', '==', this.selectedOrganizationLocation.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData = {
                id: doc.id,
                ...doc.data()
              }
              appointments.push(docData)
            });
          }).catch(err => console.log("getAllAppointments query 1 error: " + err));
      } else {
        await this.firebaseAppointmentCollection.ref
          .where('doctor_id', '==', this.currentUser.id)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              const docData = {
                id: doc.id,
                ...doc.data()
              }
              appointments.push(docData)
            });
          }).catch(err => console.log("getAllAppointments query 1 error: " + err));
      }

      this.sortAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log('getAllAppointments(): ' + JSON.stringify(error));
    }
  }

  async getTodaysAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      // Format dates
      let startDateTemp = moment().subtract(15, 'minutes').format('x');
      let endDateTemp = moment().endOf('day').format('x');

      await this.firebaseAppointmentCollection.ref
        .where('urgent', '==', false)
        .where('canceled', '==', false)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN, ConsultationType.REVIEW])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('appointment_date_timestamp', '<=', endDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getTodaysAppointments query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('urgent', '==', true)
        .where('confirmed', '==', true)
        .where('canceled', '==', false)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN, ConsultationType.REVIEW])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('appointment_date_timestamp', '<=', endDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getTodaysAppointments query 2 error: " + err));

      this.todaysAppointments.next(appointments);

    } catch (error) {
      console.log('getTodaysAppointments(): ' + JSON.stringify(error));
    }
  }

  async getNeedsAttentionAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      // Format dates
      let startDateTemp = moment().subtract(15, 'minutes').format('x');

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('info_requested', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments appointmentsInfoReq query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('info_requested', '==', true)
        .where('urgent', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments ADDITIONAL_REQUEST_APPOINTMENTS query 2 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('info_requested', '==', true)
        .where('urgent', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments ADDITIONAL_REQUEST_APPOINTMENTS query 2 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('reschedule_requested', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments RESCHEDULE_REQUESTED_APPOINTMENTS query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('urgent', '==', true)
        .where('reschedule_requested', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments RESCHEDULE_REQUESTED_APPOINTMENTS query 2 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('urgent', '==', true)
        .where('urgent_confirmed', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getNeedsAttentionAppointments CONFIRMATION_REQUIRED_APPOINTMENTS query 1 error: " + err));

      this.needsAttentionAppointments.next(appointments);
    } catch (error) {
      console.log('getNeedsAttentionAppointments(): ' + JSON.stringify(error));
    }
  }

  async getReviewAndCloseAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', true)
        .where('consultation', 'array-contains-any', [ConsultationType.REVIEW])
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getReviewAndCloseAppointments query 1 error: " + err));

      this.reviewAppointments.next(appointments);

    } catch (error) {
      console.log('getReviewAndCloseAppointments(): ' + JSON.stringify(error));
    }
  }

  async getRequestedAppointmens() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      // Format dates
      let startDateTemp = moment().subtract(15, 'minutes').format('x');

      await this.firebaseAppointmentCollection.ref
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('info_requested', '==', false)
        .where('reschedule_requested', '==', false)
        .where('needs_confirmation_by', '==', this._EXPERT)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('appointment_date_timestamp', '>=', startDateTemp)
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getRequestedAppointmens query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('urgent', '==', true)
        .where('urgent_confirmed', '==', false)
        .where('canceled', '==', false)
        .where('confirmed', '==', false)
        .where('info_requested', '==', false)
        .where('reschedule_requested', '==', false)
        .where('needs_confirmation_by', '==', this._EXPERT)
        .where('consultation', 'array-contains-any', [ConsultationType.OPEN])
        .where('doctor_id', '==', this.currentUser.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getRequestedAppointmens query 2 error: " + err));

      this.unconfirmedAppointments.next(appointments);

    } catch (error) {
      console.log('getRequestedAppointmens(): ' + JSON.stringify(error));
    }
  }

  async saveAppointment(appointment: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let appointmentToSave = appointment;

      if (appointmentToSave.created_at == null) {
        appointmentToSave.created_at = moment().format('x');
      }

      if (appointmentToSave.updated_at == null) {
        appointmentToSave.updated_at = moment().format('x');
      }

      await this.firebaseAppointmentCollection
        .add(JSON.parse(JSON.stringify(appointmentToSave)))
        .then(function (docRef) {
          appointmentToSave.id = docRef.id;
        })
        .catch(err => console.log("saveAppointment query 1 error: " + err));

      if (appointmentToSave.id != null) {
        this.relevantLabService.saveBookingLabs(appointmentToSave.id);
        this.logService.createAppointmentLog(appointmentToSave.id, LogType.APPOINTMENT_CREATED);

        const convertedTimestamp = moment(appointment.appointment_date_timestamp, 'x').format('MMM D LT');

        const emailMessage = appointment.appointment_date_timestamp != null && convertedTimestamp != null
          ? `${appointment.doctor_first_name} ${appointment.doctor_last_name} requested a consult on ${convertedTimestamp}.`
          : `${appointment.doctor_first_name} ${appointment.doctor_last_name} requested a consult.`;

        this.notificationService.sendEmailAndPush(
          UserTypeEnum.EXPERT,
          appointment.expert_id,
          'WTD - Consult Requested',
          emailMessage,
          NotificationType.APPOINTMENT_GENERAL,
          appointment.id
        );
      }

      return appointmentToSave;
    } catch (error) {
      console.log('saveAppointment(): ' + JSON.stringify(error));
    }
  }

  async cancelAppointment() {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let currentAppointment = this.appointment.value;
      currentAppointment.reschedule_requested = false;
      currentAppointment.info_requested = false;
      currentAppointment.canceled = true;
      currentAppointment.consultation = [ConsultationType.CLOSED];
      currentAppointment.date_closed = moment().format('x');

      let appointment = utils.createAppointmentObject(currentAppointment);

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          reschedule_requested: false,
          info_requested: false,
          canceled: true,
          consultation: [ConsultationType.CLOSED],
          updated_at: moment().format('x'),
          date_closed: moment().format('x')
        }).catch(err => console.log("cancelAppointment update 1 error: " + err));

      this.logService.createAppointmentLog(appointment.id, LogType.APPOINTMENT_CANCELED);
      this.appointment.next(appointment);

      return appointment;
    } catch (error) {
      console.log('cancelAppointment(): ' + JSON.stringify(error));
    }
  }

  async rescheduleAppointment(time: any, isCustomTime: boolean, newppt: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let currentAppointment = this.appointment.value;
      currentAppointment.reschedule_requested = false;
      currentAppointment.info_requested = false;

      const newAppointment = newppt;
      let appointment = utils.createAppointmentObject(newAppointment);
      appointment.is_custom_time = isCustomTime;
      appointment.appointment_date = time.date_time;
      appointment.appointment_date_timestamp = time.timestamp;
      appointment.appointment_date_id = time.id;
      appointment.zoom_meeting_id = '';
      appointment.zoom_join = '';
      appointment.zoom_start = '';
      appointment.reschedule_requested = false;
      appointment.info_requested = false;
      appointment.needs_confirmation_by = this._EXPERT;
      appointment.rescheduled = true;

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          is_custom_time: isCustomTime,
          appointment_date: time.date_time,
          appointment_date_timestamp: time.timestamp,
          appointment_date_id: time.id,
          reschedule_requested: false,
          info_requested: false,
          needs_confirmation_by: this._EXPERT,
          rescheduled: true,
          updated_at: moment().format('x')
        }).catch(err => console.log("rescheduleAppointment update 1 error: " + err));

      this.logService.createAppointmentLog(appointment.id, LogType.APPOINTMENT_CANCELED);

      const convertedTimestamp = moment(appointment.appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = appointment.appointment_date_timestamp != null && convertedTimestamp != null
        ? `${appointment.doctor_first_name} ${appointment.doctor_last_name} requsted to reschedule your ${convertedTimestamp} consult.`
        : `${appointment.doctor_first_name} ${appointment.doctor_last_name} requsted to reschedule your consult.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        appointment.expert_id,
        'WTD - Consult Rescheduled',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        appointment.id
      );

      this.appointment.next(appointment);

      return appointment;
    } catch (error) {
      console.log('rescheduleAppointment(): ' + JSON.stringify(error));
    }
  }

  async checkIfUrgentTimeIsBooked() {
    try {
      let appointmentTemp = this.appointment.getValue();
      console.log('appointmentTemp', JSON.stringify(appointmentTemp.appointment_date_timestamp))
      let appointmentCount = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', appointmentTemp.doctor_id)
        .where('canceled', '==', false)
        .where('appointment_date_timestamp', '==', appointmentTemp.appointment_date_timestamp.toString())
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            if (docData.id != appointmentTemp.id) {
              appointmentCount++;
            }
          });
        }).catch(err => console.log("checkIfUrgentTimeIsBooked query 1 error: " + err));

      return appointmentCount;
    } catch (error) {
      console.log('checkIfTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async rescheduleUrgentAppointment() {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let newAppointment = utils.createAppointmentObject(this.appointment.value);
      newAppointment.confirmed = false;
      newAppointment.urgent_confirmed = false;
      newAppointment.info_requested = false;
      newAppointment.needs_confirmation_by = this._EXPERT;

      await this.firebaseAppointmentCollection.ref
        .doc(newAppointment.id)
        .update({
          confirmed: false,
          urgent_confirmed: false,
          info_requested: false,
          needs_confirmation_by: this._EXPERT,
          updated_at: moment().format('x')
        }).catch(err => console.log("rescheduleUrgentAppointment update 1 error: " + err));


      this.appointment.next(utils.createAppointment(newAppointment));

      const convertedTimestamp = moment(newAppointment.appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = newAppointment.appointment_date_timestamp != null && convertedTimestamp != null
        ? `${newAppointment.doctor_first_name} ${newAppointment.doctor_last_name} requested to reschedule your URGENT ${convertedTimestamp} consult.`
        : `${newAppointment.doctor_first_name} ${newAppointment.doctor_last_name} requested to reschedule your URGENT consult.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        newAppointment.expert_id,
        'WTD - Urgent Consult Reschedule Requested',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        newAppointment.id
      );

      this.logService.createAppointmentLog(newAppointment.id, LogType.RESCHEDULED);

      return newAppointment;
    } catch (error) {
      console.log('rescheduleUrgentAppointment(): ' + JSON.stringify(error));
    }
  }

  async confirmUrgentAppointment(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      // Create appointment object to save to db
      let newAppointment = utils.createAppointmentObject(appointment);
      newAppointment.confirmed = true;
      newAppointment.urgent_confirmed = true;
      newAppointment.info_requested = false;
      appointment.needs_confirmation_by = this._EXPERT;

      await this.firebaseAppointmentCollection.ref
        .doc(newAppointment.id)
        .update({
          confirmed: newAppointment.confirmed,
          urgent_confirmed: newAppointment.urgent_confirmed,
          info_requested: newAppointment.info_requested,
          needs_confirmation_by: newAppointment.needs_confirmation_by,
          updated_at: moment().format('x')
        }).catch(err => console.log("confirmUrgentAppointment update 1 error: " + err));


      this.appointment.next(utils.createAppointment(newAppointment));

      const convertedTimestamp = moment(appointment.appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = appointment.appointment_date_timestamp != null && convertedTimestamp != null
        ? `${appointment.doctor_first_name} ${appointment.doctor_last_name} confirmed your ${convertedTimestamp} consult.`
        : `${appointment.doctor_first_name} ${appointment.doctor_last_name} confirmed your consult.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        appointment.expert_id,
        'WTD  - Urgent Consult Confirmed',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        appointment.id
      );

      this.logService.createAppointmentLog(newAppointment.id, LogType.APPOINTMENT_CONFIRMED);
      return newAppointment;
    } catch (error) {
      console.log('confirmUrgentAppointment(): ' + JSON.stringify(error));
    }
  }

  async resubmitAppointment(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      // Create appointment object to save to db
      let newAppointment = utils.createAppointmentObject(appointment);
      newAppointment.info_requested = false;
      newAppointment.needs_confirmation_by = this._EXPERT;

      await this.firebaseAppointmentCollection.ref
        .doc(newAppointment.id)
        .update({
          info_requested: false,
          needs_confirmation_by: this._EXPERT,
          updated_at: moment().format('x')
        }).catch(err => console.log("resubmitAppointment update 1 error: " + err));


      this.appointment.next(utils.createAppointment(newAppointment));

      const convertedTimestamp = moment(appointment.appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = appointment.appointment_date_timestamp != null && convertedTimestamp != null
        ? `${appointment.doctor_first_name} ${appointment.doctor_last_name} responded to your info request. Please review and confirm ${convertedTimestamp} consult.`
        : `${appointment.doctor_first_name} ${appointment.doctor_last_name} responded to your info request. Please review and confirm consult.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        appointment.expert_id,
        'WTD - Requested info submitted. Confirmation required',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        appointment.id
      );

      this.logService.createAppointmentLog(newAppointment.id, LogType.APPOINTMENT_RESUBMIT_HOSPITAL);

      return newAppointment;
    } catch (error) {
      console.log('resubmitAppointment(): ' + JSON.stringify(error));
    }
  }

  async resubmitJoinedAppointment() {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let updatedAppointment = this.appointment.value;
      updatedAppointment.hospital_join_time = moment().toString();
      updatedAppointment.info_requested = false;
      updatedAppointment.needs_confirmation_by = this._EXPERT;

      await this.firebaseAppointmentCollection.ref
        .doc(updatedAppointment.id)
        .update({
          hospital_join_time: moment().toString(),
          info_requested: false,
          needs_confirmation_by: this._EXPERT,
          updated_at: moment().format('x')
        }).catch(err => console.log("resubmitJoinedAppointment update 1 error: " + err));


      this.appointment.next(utils.createAppointment(updatedAppointment));

      const convertedTimestamp = moment(this.appointment.getValue().appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = this.appointment.getValue().appointment_date_timestamp != null && convertedTimestamp != null
        ? `${this.appointment.getValue().doctor_first_name} ${this.appointment.getValue().doctor_last_name} is waiting for you to begin your ${convertedTimestamp} consult.`
        : `${this.appointment.getValue().doctor_first_name} ${this.appointment.getValue().doctor_last_name} is waiting for you to begin your consult.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        this.appointment.getValue().expert_id,
        'WTD - Consult Joined',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        this.appointment.getValue().id
      );

      this.logService.createAppointmentLog(updatedAppointment.id, LogType.DOCTOR_JOINED_ZOOM);

      return updatedAppointment;
    } catch (error) {
      console.log('resubmitJoinedAppointment(): ' + JSON.stringify(error));
    }
  }

  async sendMessageAppointment(appointment: Appointment) {
    try {
      let appointmentUpdated = null;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          appointmentUpdated = docData;
        }).catch(err => console.log("sendMessageAppointment query 1 error: " + err));

      appointmentUpdated.messages.unshift(appointment.messages[0]);

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          messages: appointmentUpdated.messages,
          updated_at: moment().format('x')
        })
        .catch(err => console.log("sendMessageAppointment update 1 error: " + err));

      this.logService.createAppointmentLog(appointment.id, LogType.SUBMIT_ADDITIONAL_NOTES);
      return appointmentUpdated;
    } catch (error) {
      console.log('sendMessageAppointment(): ' + JSON.stringify(error));
    }
  }

  async sendAdminMessageAppointment(appointmentId: string, message: any) {
    try {
      const collection = this.angularFirestore.collection('Appointments').doc(appointmentId).collection("DoctorMessages");

      await collection.add({
        appointment_id: appointmentId,
        created_at: moment().format('x'),
        message: message.message,
        username: message.username,
      }).catch(err => console.log("sendAdminMessageAppointment add 1 error: " + err));



      this.logService.createAppointmentLog(appointmentId, LogType.DOCTOR_MESSAGES);
      return true;
    } catch (error) {
      console.log('sendMessageAppointment(): ' + JSON.stringify(error));
    }
  }

  async closeConsult() {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let newTempAppointment = {
        date_closed: moment().format('x'),
        consultation: [this._CLOSED],
      }
      this.updateTempAppointment(newTempAppointment);

      await this.firebaseAppointmentCollection.ref
        .doc(this.appointment.value.id)
        .update({
          date_closed: newTempAppointment.date_closed,
          consultation: newTempAppointment.consultation,
          updated_at: moment().format('x')
        }).catch(err => console.log("closeConsult update 1 error: " + err));

      this.logService.createAppointmentLog(this.appointment.getValue().id, LogType.APPOINTMENT_CLOSED);
      const review = this.appointment.getValue().expectation;
      if (!review) {
        this.notificationService.sendEmail(
          'WTD Admin - Consult Review',
          'admin@wtd.com',
          'An appointment has been closed and needs to be addressed.  Please log in to review the appointment.',
          `Admin`
        );
      }
    } catch (error) {
      console.log('closeConsult(): ' + JSON.stringify(error));
    }
  }

  async saveUrgentAppointment(appointment) {
    try {

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let updatedAppointment = appointment;
      updatedAppointment.consultation[0] = 'open';
      updatedAppointment.is_custom_time = false;
      updatedAppointment.needs_confirmation_by = this._EXPERT;

      let { id, ...appointmentToSave } = updatedAppointment;

      if (appointmentToSave.created_at == null) {
        appointmentToSave.created_at = moment().format('x');
      }

      if (appointmentToSave.updated_at == null) {
        appointmentToSave.updated_at = moment().format('x');
      }

      await this.firebaseAppointmentCollection
        .add(JSON.parse(JSON.stringify(appointmentToSave)))
        .then(function (docRef) {
          appointmentToSave.id = docRef.id;
        })
        .catch(err => console.log("saveUrgentAppointment save 1 error: " + err));

      if (appointmentToSave.id != null) {
        // this.logService.createAppointmentLog(appointmentToSave.id, LogType.APPOINTMENT_CREATED);
        // this.relevantLabService.saveBookingLabs(appointmentToSave.id);
        // this.appointment.next(utils.createAppointment(appointmentToSave));
        return appointmentToSave;
      } else {
        return null;
      }
    } catch (error) {
      console.log('saveUrgentAppointment(): ' + JSON.stringify(error));
    }
  }

  async checkIfExpertTimeIsBooked(time: any, expertID: string) {
    try {
      let appointments = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('expert_id', '==', expertID)
        .where('appointment_date_timestamp', '==', time.timestamp.toString())
        .where('canceled', '==', false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointments++;
          });
        }).catch(err => console.log("checkIfExpertTimeIsBooked query 1 error: " + err));

      return appointments;
    } catch (error) {
      console.log('checkIfExpertTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async checkIfDoctorTimeIsBooked(time: any, doctorID: string) {
    try {
      let appointments = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', doctorID)
        .where('appointment_date_timestamp', '==', time.timestamp.toString())
        .where('canceled', '==', false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointments++;
          });
        }).catch(err => console.log("checkIfDoctorTimeIsBooked query 1 error: " + err));

      return appointments;
    } catch (error) {
      console.log('checkIfDoctorTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async updateAppointmentAsync(appointment: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let newTempAppointment = appointment;

      await this.firebaseAppointmentCollection.ref
        .doc(this.appointment.value.id)
        .update({
          urgent_notes: newTempAppointment.urgent_notes,
          info_requested: newTempAppointment.info_requested,
          needs_confirmation_by: newTempAppointment.needs_confirmation_by,
          updated_at: moment().format('x')
        }).catch(err => console.log("updateAppointmentAsync update 1 error: " + err));

      this.updateTempAppointment(newTempAppointment);
    } catch (error) {
      console.log('updateAppointmentAsync(): ' + JSON.stringify(error));
    }
  }

  updateAppointmentAndSort(appointments: any[], appointmentList) {
    // Sort appointments before saving
    const sorteddAppointments = appointments.sort((a, b) => parseInt(moment(a.appointment_date).format('x').toString()) - parseInt(moment(b.appointment_date).format('x').toString()));

    // Save sorted appointments to appropriate list
    switch (appointmentList) {
      case appointmentlistenum.ALL_APPOINTMENTS:
        this.allAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.TODAYS_APPOINTMENTS:
        this.todaysAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.UNCONFIRMED_APPOINTMENTS:
        this.unconfirmedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.CONFIRMATION_REQUIRED_APPOINTMENTS:
        this.confirmationRequiredAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.ADDITIONAL_REQUEST_APPOINTMENTS:
        this.additionalRequestAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.RESCHEDULE_REQUESTED_APPOINTMENTS:
        this.rescheduleRequestedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.REVIEW_APPOINTMENTS:
        this.reviewAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.CLOSED_APPOINTMENTS:
        this.closedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.CONFIRMED_APPOINTMENTS:
        this.confirmedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.NEEDS_ATTENTION:
        this.needsAttentionAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.ALL_UPCOMING_APPOINTMENTS:
        this.allUpcomingAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.ALL_PAST_APPOINTMENTS:
        this.allPastAppointments.next(sorteddAppointments);
        break;
      default:
        break;
    }
  }

  setUser(currentUser: any) {
    this.currentUser = currentUser;
    this.getAllAppointments();
  }

  sortAppointments(scheduleCollection) {
    let appointmentsTemp: Appointment[] = [];
    let todaysAppointmentsTemp: Appointment[] = [];
    let unconfirmedAppointmentsTemp: Appointment[] = [];
    let additionalRequestAppointmentsTemp: Appointment[] = [];
    let needsAttentionTemp: Appointment[] = [];
    let confirmationRequiredTemp: Appointment[] = [];
    let rescheduleRequestedAppointmentsTemp: Appointment[] = [];
    let reviewAppointmentsTemp: Appointment[] = [];
    let confirmedAppointmentsTemp: Appointment[] = [];
    let closedAppointmentsTemp: Appointment[] = [];
    let allPastAppointmentsTemp: Appointment[] = [];
    let allUpcomingAppointmentsTemp: Appointment[] = [];

    for (let i = 0; i < scheduleCollection.length; i++) {
      let appointment = utils.createAppointment(scheduleCollection[i]);
      let pastTime = false;
      let pastExactTime = false;
      const currentDate = new Date(appointment.appointment_date);
      const todaysDate = new Date();

      //Check if time is within 15 minutes
      let momentDate = moment(appointment.appointment_date);
      let currentTime = moment();
      let timeDiff = momentDate.diff(currentTime, 'minutes');
      if (timeDiff < -15) { pastTime = true; }
      if (timeDiff < 0) { pastExactTime = true; }

      if (!appointment.canceled) {
        if (this.isTodaysAppointment(appointment, currentDate, todaysDate, pastTime)) {
          todaysAppointmentsTemp.push(appointment);
        }

        if (this.isReviewAppointment(appointment, pastExactTime)) {
          reviewAppointmentsTemp.push(appointment);
        }

        if (this.isConfirmedAppointment(appointment, currentDate, todaysDate, pastTime)) {
          confirmedAppointmentsTemp.push(appointment);
        }

        if (this.isUnconfirmedAppointment(appointment, pastTime)) {
          unconfirmedAppointmentsTemp.push(appointment);
        }

        if (this.isAdditionalOrNeedsAttentionAppointment(appointment, pastTime)) {
          additionalRequestAppointmentsTemp.push(appointment);
          needsAttentionTemp.push(appointment);
        }

        if (this.isConfirmationRequiredOrNeedsAttention(appointment, pastTime)) {
          confirmationRequiredTemp.push(appointment);
          needsAttentionTemp.push(appointment);
        }

        if (this.isRescheduleRequestedOrNeedsAttention(appointment, pastTime)) {
          rescheduleRequestedAppointmentsTemp.push(appointment);
          needsAttentionTemp.push(appointment);
        }

        if (appointment.consultation[0] == 'closed') {
          closedAppointmentsTemp.push(appointment);
        }
      }

      appointmentsTemp.push(appointment);

      if (pastTime || appointment.canceled) {
        allPastAppointmentsTemp.push(appointment);
      } else {
        allUpcomingAppointmentsTemp.push(appointment);
      }
    }

    this.updateAppointmentAndSort(appointmentsTemp, appointmentlistenum.ALL_APPOINTMENTS);
    this.updateAppointmentAndSort(todaysAppointmentsTemp, appointmentlistenum.TODAYS_APPOINTMENTS);
    this.updateAppointmentAndSort(additionalRequestAppointmentsTemp, appointmentlistenum.ADDITIONAL_REQUEST_APPOINTMENTS);
    this.updateAppointmentAndSort(confirmationRequiredTemp, appointmentlistenum.CONFIRMATION_REQUIRED_APPOINTMENTS);
    this.updateAppointmentAndSort(rescheduleRequestedAppointmentsTemp, appointmentlistenum.RESCHEDULE_REQUESTED_APPOINTMENTS);
    this.updateAppointmentAndSort(unconfirmedAppointmentsTemp, appointmentlistenum.UNCONFIRMED_APPOINTMENTS);
    this.updateAppointmentAndSort(reviewAppointmentsTemp, appointmentlistenum.REVIEW_APPOINTMENTS);
    this.updateAppointmentAndSort(closedAppointmentsTemp, appointmentlistenum.CLOSED_APPOINTMENTS);
    this.updateAppointmentAndSort(confirmedAppointmentsTemp, appointmentlistenum.CONFIRMED_APPOINTMENTS);
    this.updateAppointmentAndSort(needsAttentionTemp, appointmentlistenum.NEEDS_ATTENTION);
    this.updateAppointmentAndSort(allUpcomingAppointmentsTemp, appointmentlistenum.ALL_UPCOMING_APPOINTMENTS);
    this.updateAppointmentAndSort(allPastAppointmentsTemp, appointmentlistenum.ALL_PAST_APPOINTMENTS);
  }

  isRescheduleRequestedOrNeedsAttention(appointment, pastTime) {
    return appointment.consultation[0] == 'open'
      && !pastTime
      && !appointment.info_requested
      && appointment.reschedule_requested
      && !appointment.canceled;
  }

  isConfirmationRequiredOrNeedsAttention(appointment, pastTime) {
    return appointment.consultation[0] == 'open'
      && !pastTime
      && appointment.info_requested
      && !appointment.reschedule_requested
      && appointment.urgent
      && !appointment.confirmed
      ||
      appointment.consultation[0] == 'open'
      && !pastTime
      && appointment.urgent_confirmed
      && appointment.urgent
      && !appointment.confirmed;
  }

  isAdditionalOrNeedsAttentionAppointment(appointment, pastTime) {
    return appointment.consultation[0] == 'open'
      && !pastTime && appointment.info_requested
      && !appointment.reschedule_requested
      && !appointment.urgent
      && !appointment.confirmed;
  }

  isUnconfirmedAppointment(appointment, pastTime) {
    return appointment.consultation[0] == 'open'
      && !pastTime
      && !appointment.info_requested
      && !appointment.reschedule_requested
      && !appointment.canceled
      && !appointment.confirmed
      ||
      appointment.urgent
      && !appointment.urgent_confirmed
      && !appointment.canceled
      && !appointment.confirmed;
  }

  isConfirmedAppointment(appointment, currentDate, todaysDate, pastTime) {
    return appointment.consultation[0] == 'open'
      && !(currentDate.getDate() === todaysDate.getDate())
      && !pastTime
      && appointment.confirmed;
  }

  isReviewAppointment(appointment, pastExactTime) {
    return pastExactTime
      && appointment.consultation[0] != 'closed'
      && appointment.hospital_join_time != null
      && appointment.confirmed;
  }

  isTodaysAppointment(appointment, currentDate, todaysDate, pastTime) {
    return currentDate.getDate() === todaysDate.getDate()
      && !pastTime
      && !appointment.info_requested
      && !appointment.reschedule_requested
      && appointment.consultation[0] != 'closed'
      && appointment.confirmed;
  }

  updateAppointment(appointment: any) {
    let newAppointment = utils.createAppointment(appointment);
    this.appointment.next(newAppointment);
  }

  updateTempAppointment(changes: Partial<Appointment>) {
    const appointment = this.appointment.getValue();

    const newTempAppointment: Appointment = {
      ...appointment,
      ...changes
    }
    this.appointment.next(newTempAppointment);
  }
}